import { grooveToSteps } from './grooves';

export function countLabel(step, stepsPerBar = 16) {
  const local = ((Number(step) % stepsPerBar) + stepsPerBar) % stepsPerBar;
  if (stepsPerBar === 12) return [1, 'trip', 'let', 2, 'trip', 'let', 3, 'trip', 'let', 4, 'trip', 'let'][local];
  return [1, 'e', '&', 'a', 2, 'e', '&', 'a', 3, 'e', '&', 'a', 4, 'e', '&', 'a'][local];
}

export function spokenCountLabel(step, stepsPerBar = 16) {
  const local = ((Number(step) % stepsPerBar) + stepsPerBar) % stepsPerBar;
  const beat = Math.floor(local / (stepsPerBar === 12 ? 3 : 4)) + 1;
  const part = countLabel(local, stepsPerBar);
  return typeof part === 'number' ? String(part) : `${part}-of-${beat}`;
}

export function availableLayers(groove, availableRows) {
  return (groove?.layers || []).filter((layer) => availableRows.has(layer.row));
}

const layerHitSet = (layer) => new Set([...(layer?.steps || []), ...Object.keys(layer?.ratchets || {}).map(Number)]);

export function guideGhosts(groove, layerIndex, patternBars, availableRows) {
  const layers = availableLayers(groove, availableRows);
  const layer = layers[layerIndex];
  if (!layer) return { steps: {}, ratchets: {}, missingRows: [] };
  return grooveToSteps({ ...groove, layers: [layer] }, availableRows, patternBars);
}

export function layerComplete(pattern, groove, layerIndex, availableRows) {
  const layer = availableLayers(groove, availableRows)[layerIndex];
  if (!layer) return false;
  const expected = guideGhosts(groove, layerIndex, pattern.patternBars, availableRows).steps[layer.row] || [];
  return expected.every((velocity, step) => !(velocity > 0) || pattern.steps?.[layer.row]?.[step] > 0);
}

export function challengeRow(groove, availableRows) {
  if (availableRows.has(groove?.challenge?.row)) return groove.challenge.row;
  return (groove?.layers || []).find((layer) => layer.layer === 'anchor' && availableRows.has(layer.row))?.row || null;
}

export function challengeCheck(pattern, groove, availableRows) {
  const row = challengeRow(groove, availableRows);
  if (!row) return { passed: false, row: null, missing: [], extras: 0 };
  const anchors = (groove.layers || []).filter((layer) => layer.layer === 'anchor' && layer.row === row).flatMap((layer) => [...layerHitSet(layer)]);
  const required = new Set(anchors);
  const phraseLength = (groove.grooveBars || 1) * groove.stepsPerBar;
  const actual = new Set((pattern.steps?.[row] || []).slice(0, phraseLength).map((velocity, step) => velocity > 0 ? step : -1).filter((step) => step >= 0));
  const missing = [...required].filter((step) => !actual.has(step));
  const extras = [...actual].filter((step) => !required.has(step)).length;
  return { passed: missing.length === 0 && extras <= 1, row, missing, extras };
}

export function challengeHint(result, stepsPerBar) {
  if (result.missing.length) return `One is missing on the ${spokenCountLabel(result.missing[0], stepsPerBar)}.`;
  return 'That row has more than one extra hit. Leave a little space.';
}

const hitsInBar = (pattern, row, bar) => (pattern.steps?.[row] || []).slice(bar * pattern.stepsPerBar, (bar + 1) * pattern.stepsPerBar);
const anyAt = (pattern, rows, index) => rows.some((row) => pattern.steps?.[row]?.[index] > 0);

/** Ordered, rule-based feedback. Unavailable kit rows are ignored. */
export function feelCheck(pattern, groove, kitRows) {
  const available = kitRows instanceof Set ? kitRows : new Set(kitRows || []);
  const bars = pattern.patternBars || 1;
  const grid = pattern.stepsPerBar === 12 ? 12 : 16;
  const findings = [];
  if (available.has('kick') && Array.from({ length: bars }, (_, bar) => pattern.steps?.kick?.[bar * grid] > 0).every((hit) => !hit)) findings.push({ id: 'no-one', severity: 'warning', text: 'No kick on 1. The beat loses its home.' });
  if (available.has('kick')) {
    const stackedBars = Array.from({ length: bars }, (_, bar) => Array.from({ length: grid }, (_, step) => {
      const index = bar * grid + step;
      return pattern.steps?.kick?.[index] > 0 && anyAt(pattern, ['snare', 'clap'].filter((row) => available.has(row)), index);
    }).some(Boolean)).filter(Boolean).length;
    if (stackedBars > bars / 2) findings.push({ id: 'stack', severity: 'warning', text: 'Kick and snare are stacked. Move one so both can hit.' });
  }
  if (available.has('hat') && Array.from({ length: bars }, (_, bar) => {
    const hits = hitsInBar(pattern, 'hat', bar).filter((velocity) => velocity > 0);
    return hits.length >= 8 && hits.every((velocity) => velocity >= 0.8);
  }).some(Boolean)) findings.push({ id: 'flat-hats', severity: 'tip', text: 'Every hat is full volume. Try Shift-click for soft hits on the e and a.' });
  const backbeatRows = ['snare', 'clap', 'rim'].filter((row) => available.has(row));
  if (backbeatRows.length && !backbeatRows.some((row) => (pattern.steps?.[row] || []).some((velocity) => velocity > 0))) findings.push({ id: 'no-backbeat', severity: 'warning', text: 'No snare or clap yet. Add the backbeat.' });
  if ((groove?.family === 'boombap' || groove?.id === 'lofi') && Number(pattern.swing || 0) === 0) findings.push({ id: 'swing-zero', severity: 'tip', text: 'This style wants swing. Try around 58%.' });
  if (available.has('hat') && available.has('openhat') && (pattern.steps?.hat || []).some((velocity, step) => velocity > 0 && pattern.steps?.openhat?.[step] > 0)) findings.push({ id: 'choke', severity: 'warning', text: 'Open and closed hat on the same step. Pick one.' });
  if (available.has('kick') && Array.from({ length: bars }, (_, bar) => hitsInBar(pattern, 'kick', bar).filter((velocity) => velocity > 0).length > (grid === 12 ? 8 : 10)).some(Boolean)) findings.push({ id: 'busy-kick', severity: 'tip', text: 'That kick is busy. Leave some space.' });
  if (['dembow', 'perreo', 'dirty-dembow'].includes(groove?.id) && available.has('snare')) {
    const targets = (groove.layers || []).find((layer) => layer.row === 'snare' && layer.layer === 'anchor')?.steps || [];
    if (targets.filter((step) => pattern.steps?.snare?.[step] > 0).length < 3) findings.push({ id: 'dembow-missing', severity: 'warning', text: 'The dembow needs the snare on boom, ch-boom, chick.' });
  }
  return findings.length ? findings : [{ id: 'all-good', severity: 'good', text: 'That groove holds up. Nice.' }];
}

export function readCoachProgress(storage) {
  try {
    const target = storage === undefined ? globalThis.localStorage : storage;
    return JSON.parse(target?.getItem('ba_coach_progress') || '{}') || {};
  } catch { return {}; }
}

export function recordCoachPass(family, storage) {
  const progress = readCoachProgress(storage);
  progress[family] = Number(progress[family] || 0) + 1;
  try {
    const target = storage === undefined ? globalThis.localStorage : storage;
    target?.setItem('ba_coach_progress', JSON.stringify(progress));
  } catch { /* Storage is optional. */ }
  return progress[family];
}

export const startsOnChallenge = (family, storage) => Number(readCoachProgress(storage)[family] || 0) >= 3;
